import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import Parallax from "./Parallax";

const STEPS = [
  { n: "01", title: "Understand the work", desc: "We clarify the team's purpose, roles, ways of working, and the outcomes you're looking for." },
  { n: "02", title: "Choose a starting model", desc: "We help identify the engagement structure that fits your current stage - nothing more, nothing less." },
  { n: "03", title: "Build the local foundation", desc: "We coordinate the agreed hiring, employment, operational, and infrastructure setup on the ground." },
  { n: "04", title: "Stay aligned as you grow", desc: "We review the arrangement with you as the team's scope, size, and needs evolve." },
];

export default function Process() {
  const railRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: railRef, offset: ["start 80%", "end 45%"] });
  const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section id="process" className="relative -mt-8 rounded-t-[48px] py-20 sm:py-28 lg:py-32 bg-[#EDE6FC]" data-testid="process-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-3xl mb-16"
        >
          <span className="inline-flex items-center rounded-full border border-[#5B4199]/40 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#5B4199] mb-6">
            How the engagement works
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-7xl font-extrabold tracking-tight text-[#0B132B] leading-[1.04]">
            From operating decision to connected team.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[#475569] leading-relaxed">
            A straightforward path from conversation to a functioning India team - without promising
            timelines we can't control.
          </p>
        </motion.div>

        <div ref={railRef} className="relative">
          <div aria-hidden="true" className="hidden lg:block absolute top-[24px] left-8 right-8 h-[3px] bg-white/80 rounded-full" />
          <motion.div
            aria-hidden="true"
            style={{ scaleX: lineScale }}
            className="hidden lg:block absolute top-[24px] left-8 right-8 h-[3px] bg-[#5B4199] rounded-full origin-left"
            data-testid="process-progress-line"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <Parallax key={s.n} distance={i % 2 === 0 ? 28 : -22} className="h-full">
                <motion.div
                  initial={{ opacity: 0, y: 32 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={`card-jeton relative h-full rounded-[28px] bg-white shadow-[0_24px_56px_-18px_rgba(76,29,149,0.22)] p-7 hover:rotate-0 ${
                    i % 2 === 0 ? "lg:-rotate-1" : "lg:rotate-1"
                  }`}
                  data-testid={`process-step-${i + 1}`}
                >
                  <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-[#5B4199] font-mono-tech text-sm text-white mb-6 shadow-[0_12px_28px_-8px_rgba(91,65,153,0.55)]">
                    {s.n}
                  </span>
                  <h3 className="font-display text-lg sm:text-xl font-extrabold text-[#0B132B] mb-3">{s.title}</h3>
                  <p className="text-sm sm:text-[15px] text-[#475569] leading-relaxed">{s.desc}</p>
                </motion.div>
              </Parallax>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
